import { Box } from "@mui/material";
import type { FC } from "react";
import { useIsHalloweenSeason } from "@/hooks/useIsHalloweenSeason";
import { localStorageService } from "@/services/LocalStorageService";
import { useThemePreference } from "@/theme/ThemePreferenceProvider";
import { DEFAULT_LOGO, LOGO_OPTION_BY_ID } from "@/theme/tokens";
import { SeasonalLogoHat } from "@/ui/Seasonal/SeasonalLogoHat";
import { INHOUSE_LOGO_MARK_RATIO, InHouseLogo, SIGNIN_LOGO_HEIGHT_PX } from "./InHouseLogo";

// Turns the dark InHouse mark white, for use on a colored background.
const WHITE_LOGO_FILTER = "brightness(0) invert(1)";

interface SigninLogoProps {
	/** Set when the logo sits on a dark or brand-colored background. */
	onDark?: boolean;
}

/**
 * The logo on the signed-out screens (the sign-in pages, the maintenance screen): the InHouse logo, unless a
 * department look (the UKG demo) saved a logo on this device, so that look stays after logout. A logo support picked in Appearance is not
 * shown here.
 */
export const SigninLogo: FC<SigninLogoProps> = ({ onDark }) => {
	const { logo: savedLogo } = useThemePreference();
	const logo = localStorageService.getDepartmentLookAppliedFor() === null ? DEFAULT_LOGO : savedLogo;
	const testId = `signin-logo-${logo}`;
	const isHalloweenSeason = useIsHalloweenSeason();

	if (logo === "inhouse") {
		return (
			// The hat wrapper shrinks to the logo, so the hat lands on the mark; every caller centers the logo.
			<SeasonalLogoHat decorated={isHalloweenSeason} markWidthRatio={INHOUSE_LOGO_MARK_RATIO} dataTestId="signin-logo-seasonal">
				<Box data-testid={testId} sx={{ filter: onDark ? WHITE_LOGO_FILTER : undefined }}>
					<InHouseLogo />
				</Box>
			</SeasonalLogoHat>
		);
	}

	// Branded logos have their own colors and background, so they are shown as they are.
	const { header, label } = LOGO_OPTION_BY_ID[logo];
	return <Box component="img" data-testid={testId} src={header} alt={label} sx={{ height: SIGNIN_LOGO_HEIGHT_PX, width: "auto", display: "block" }} />;
};
